import React, { useState, useEffect } from "react";
import { DataTable, Modal, EmptyState as Empty } from "@/components/ui";
import { fmtDate } from "@/lib/formatters";
import { Eye, Check, X, FileText } from "lucide-react";
import { estimationService } from "@/services/estimation_service";
import toast from "react-hot-toast";
import api, { API_BASE_URL } from "@/services/api";
import { FilterSection } from "@/components/ui/FilterSection";

export default function OrderApprovalTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: "all", fromDate: "", toDate: "", status: "ORDER_PLACED" });

  const STATUS_OPTIONS = [
    { value: "ORDER_PLACED", label: "Pending Approval" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" }
  ];

  const handleSetFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const [estimationsRes, paymentsRes] = await Promise.all([
        estimationService.getEstimations({ status: filters.status !== 'all' ? filters.status : undefined }),
        api.get('/accounts/recieved_amounts')
      ]);
      
      const paymentsData = paymentsRes.data?.data || [];
      const paymentMap = {};
      paymentsData.forEach(p => {
        const estId = typeof p.estimation_id === 'object' ? p.estimation_id?._id : p.estimation_id;
        if (estId) {
          const totalPaid = (p.payments || []).reduce((sum, pay) => sum + (Number(pay.amount) || 0), 0);
          paymentMap[estId] = totalPaid;
        }
      });

      if (estimationsRes.success && estimationsRes.data) {
        const mappedRequests = estimationsRes.data.map(r => ({
          ...r,
          advancePaid: paymentMap[r._id] || 0
        }));
        
        setRequests(mappedRequests.filter(r => 
          r.status === 'ORDER_PLACED' || r.status === 'APPROVED' || r.status === 'REJECTED' || r.status === 'PENDING_APPROVAL'
        ));
      }
    } catch (err) {
      console.error("Failed to fetch order requests:", err);
      toast.error("Failed to fetch order requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filters.status]);

  const handleActionClick = (req, type) => {
    setSelectedRequest(req);
    setActionType(type);
    setIsModalOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      if (actionType === "approve") {
        await estimationService.updateEstimationStatus(selectedRequest._id, "APPROVED", "");
        toast.success("Order approved successfully");
      } else if (actionType === "reject") {
        await estimationService.updateEstimationStatus(selectedRequest._id, "REJECTED", "");
        toast.success("Order rejected successfully");
      }
      setIsModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      console.error("Action failed:", err);
      toast.error("Failed to execute action");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDirectApprove = async (req) => {
    try {
      await estimationService.updateEstimationStatus(req._id, "APPROVED", "");
      toast.success("Order approved successfully");
      fetchRequests();
    } catch (err) {
      console.error("Approval action failed:", err);
      toast.error("Failed to approve order");
    }
  };

  const filteredRequests = React.useMemo(() => {
    let result = [...requests];
    
    try {
      const storeRef = JSON.parse(localStorage.getItem('company_user') || '{}').storeRef;
      if (storeRef && storeRef !== 'all') {
        result = result.filter(r => 
          r.store?._id === storeRef || 
          r.store === storeRef || 
          r.customer_id?.store?._id === storeRef || 
          r.customer_id?.store === storeRef
        );
      }
    } catch(err){}

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(r => 
        r.customer_id?.sdId?.toLowerCase().includes(lower) ||
        r.customer_id?.name?.toLowerCase().includes(lower)
      );
    }
    if (filters.status && filters.status !== "all") {
      result = result.filter(r => String(r.status).toUpperCase() === String(filters.status).toUpperCase());
    }
    return result;
  }, [requests, search, filters]);

  const calculateGrandTotal = (currentOrder) => {
    if (!currentOrder || !currentOrder.items) return 0;
    const subtotal = currentOrder.items.reduce((sum, item) => {
      const price = Number(item.price) || Number(item.unit_price) || 0;
      const qty = Number(item.quantity) || 1;
      return sum + (price * qty);
    }, 0);
    const discount = Number(currentOrder.discount_percent) || 0;
    const discountAmount = Math.round(subtotal * (discount / 100));
    const taxableValue = subtotal - discountAmount;
    const gstValue = Number(currentOrder.gst_value) || 0;
    const gstAmount = Math.round(taxableValue * (gstValue / 100));
    return taxableValue + gstAmount;
  };

  const columns = [
    {
      header: "SD ID",
      key: "sdId",
      render: (r) => (
        <span className="text-[10px] font-bold text-brand-orange whitespace-nowrap">
          {r.customer_id?.sdId || "—"}
        </span>
      ),
    },
    {
      header: "Customer Name",
      key: "customerName",
      render: (r) => (
        <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate">
          {r.customer_id?.name || r.customer_id?.customerName || "—"}
        </span>
      ),
    },
    {
      header: "Items Count",
      key: "itemsCount",
      render: (r) => (
        <span className="text-[12px] font-semibold text-slate-700">
          {r.items?.length || 0} items
        </span>
      ),
    },
    {
      header: "Date",
      key: "createdAt",
      render: (r) => (
        <span className="text-[11px] font-medium text-slate-600">
          {fmtDate(r.createdAt)}
        </span>
      ),
    },
      // {
      //   header: "Estimation Amount",
      //   key: "estimationAmount",
      //   render: (r) => (
      //     <span className="text-[12px] font-semibold text-slate-800">
      //       ₹{Number(r.amount || r.grandTotal || calculateGrandTotal(r)).toLocaleString('en-IN')}
      //     </span>
      //   ),
      // },
    {
      header: "Advance Paid",
      key: "advancePaid",
      render: (r) => (
        <span className="text-[12px] font-semibold text-emerald-600">
          ₹{Number(r.advancePaid || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      header: "Status",
      key: "status",
      render: (r) => {
        const s = r.status;
        const colors = {
          ORDER_PLACED: "bg-amber-100 text-amber-700 border-amber-200",
          APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
          REJECTED: "bg-red-100 text-red-700 border-red-200",
        };
        const displayText = s === 'ORDER_PLACED' ? 'PENDING APPROVAL' : s.replace(/_/g, " ");
        return (
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider border whitespace-nowrap ${colors[s] || colors.ORDER_PLACED}`}>
            {displayText}
          </span>
        );
      },
    },
    {
      header: "Actions",
      key: "actions",
      render: (r) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedPdfUrl(`${API_BASE_URL}/api/estimations/${r._id}/stream-pdf`);
                setPdfModalOpen(true);
              }}
              className="p-1 text-slate-500 hover:bg-slate-50 rounded transition-colors cursor-pointer"
              title="View PDF"
            >
              <Eye size={18} strokeWidth={2.5} />
            </button>
            {r.status === "ORDER_PLACED" && (
              <>
                <button
                  onClick={() => handleDirectApprove(r)}
                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                  title="Approve Order"
                >
                  <Check size={18} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => handleActionClick(r, "reject")}
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                  title="Reject Order"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col rounded-2xl overflow-hidden min-w-0">
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={handleSetFilters}
          showExport={false}
          statusOptions={STATUS_OPTIONS}
          statusPlaceholder="All Statuses"
        />
      </div>

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredRequests}
          isLoading={loading}
          className="border-none shadow-none rounded-none"
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          actionType === 'view'
            ? 'Order Details'
            : actionType === 'approve'
            ? 'Approve Order'
            : 'Reject Order'
        }
        size="max-w-4xl"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-3 p-4 bg-slate-50/50 rounded-xl border border-slate-100">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-[11px] font-bold text-slate-600 uppercase">Customer</span>
              <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.customer_id?.name || selectedRequest?.customer_id?.customerName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[11px] font-medium text-slate-500 uppercase">SD ID</span>
              <span className="text-[11px] font-bold text-brand-orange uppercase">{selectedRequest?.customer_id?.sdId || "—"}</span>
            </div>
            {actionType === 'view' && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Requested Date</span>
                  <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.createdAt ? fmtDate(selectedRequest.createdAt) : "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Status</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-brand-blue">{selectedRequest?.status?.replace(/_/g, " ")}</span>
                </div>
              </>
            )}
          </div>

          <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto shadow-sm bg-white">
            <table className="w-full text-left border-collapse whitespace-nowrap">
              <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <tr className="divide-x divide-slate-100">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Category/Type</th>
                  <th className="px-3 py-2 text-center">Quantity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-[11px] font-medium text-slate-700">
                {selectedRequest?.items?.map((it, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors divide-x divide-slate-50">
                    <td className="px-3 py-2 font-semibold text-slate-800">
                      {it.item_details?.product || "Unknown Product"}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-600">
                      {it.item_type}
                    </td>
                    <td className="px-3 py-2 font-bold text-slate-800 text-center">
                      {it.qty}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3 pt-4">
            {actionType === 'view' ? (
              <button
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary flex-1 cursor-pointer bg-slate-800 text-white hover:bg-slate-700"
              >
                Close Details
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary flex-1 cursor-pointer"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className={`btn-primary flex-1 flex justify-center items-center cursor-pointer ${
                    actionType === "reject" ? "bg-red-600 hover:bg-red-700 border-red-600" : "bg-brand-orange hover:bg-orange-600 border-brand-orange"
                  }`}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    actionType === "approve" ? "Approve" : "Reject Order"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* PDF View Modal */}
      <Modal
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        title="View Estimation PDF"
        size="max-w-4xl"
      >
        <div className="w-full h-[70vh] bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
          {selectedPdfUrl ? (
            <iframe src={selectedPdfUrl} className="w-full h-full" title="Estimation PDF" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500">
              Loading PDF...
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
